/**
 * JoinedBanner (Iter 9.40 - Sync Architecture)
 * Reads ?joined=host|waitlist|coming_soon|live from URL and shows a contextual
 * banner on /me right after an InterestPopup redirect. Auto-dismisses on close
 * AND removes the query param so it doesn't re-fire.
 */
import { useEffect, useState } from "react";
import { X, PartyPopper, Mail, Clock } from "lucide-react";

export default function JoinedBanner() {
  const [state, setState] = useState(null);

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const joined = params.get("joined");
      if (!joined) return;
      setState({
        type: joined,
        category: params.get("cat") || "",
        city: params.get("city") || "",
        org: params.get("org") || "",
      });
    } catch {}
  }, []);

  const dismiss = () => {
    try {
      const params = new URLSearchParams(window.location.search);
      ["joined", "cat", "city", "org"].forEach((k) => params.delete(k));
      const next = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (next ? "?" + next : ""));
    } catch {}
    setState(null);
  };

  if (!state) return null;

  const copy = (() => {
    switch (state.type) {
      case "host":
        return {
          Icon: PartyPopper,
          title: state.org ? `Welcome, ${state.org}` : "Welcome, host",
          body: state.category
            ? `Your ${state.category} dashboard is being set up. We'll email you once it's live.`
            : "Your dashboard is being set up. We'll email you once it's live.",
          tone: "bg-emerald-50 border-emerald-200 text-emerald-900",
        };
      case "waitlist":
        return {
          Icon: Clock,
          title: "You're on the waitlist",
          body: `We're launching ${state.category || "this category"}${state.city ? ` in ${state.city}` : ""} city by city. We'll email you the moment it's your turn.`,
          tone: "bg-amber-50 border-amber-200 text-amber-900",
        };
      case "coming_soon":
      case "notify":
        return {
          Icon: Mail,
          title: "We'll notify you",
          body: `We'll email you the moment ${state.category || "this category"}${state.city ? ` launches in ${state.city}` : ""} goes live.`,
          tone: "bg-blue-50 border-blue-200 text-blue-900",
        };
      case "live":
        return {
          Icon: PartyPopper,
          title: "You're in",
          body: "Set up your preferences on the Defaults tab so hosts know you before you even arrive.",
          tone: "bg-emerald-50 border-emerald-200 text-emerald-900",
        };
      default:
        return null;
    }
  })();

  if (!copy) return null;
  const { Icon, title, body, tone } = copy;

  return (
    <div
      className={`rounded-xl border p-4 flex items-start gap-3 ${tone}`}
      data-testid={`joined-banner-${state.type}`}
    >
      <Icon className="w-5 h-5 mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm">{title}</p>
        <p className="text-xs mt-0.5 leading-relaxed">{body}</p>
      </div>
      <button
        onClick={dismiss}
        className="opacity-60 hover:opacity-100 transition-opacity flex-shrink-0"
        aria-label="Dismiss"
        data-testid="joined-banner-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
